import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Fieldset } from 'primereact/fieldset';
import apiFetch from '../../auth/apiFetch';
import { properties } from '../../properties';

const emptyPosition = { id: null, name: '', section: '', displayOrder: 0 };

export function BoardPositionsList() {
    const [positions, setPositions] = useState([]);
    const [position, setPosition] = useState(emptyPosition);
    const [dialog, setDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [apiError, setApiError] = useState('');
    const toast = useRef(null);

    useEffect(() => { load(); }, []);

    async function load() {
        try {
            const res = await apiFetch(`${properties.baseURL}${properties.boardPositionsPath}`);
            if (!res.ok) throw new Error(`Erro ${res.status} ao carregar cargos`);
            setPositions(await res.json());
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: e.message, life: 5000 });
        }
    }

    function openNew() {
        setPosition(emptyPosition);
        setApiError('');
        setDialog(true);
    }

    function openEdit(row) {
        setPosition({ ...row });
        setApiError('');
        setDialog(true);
    }

    function openDelete(row) {
        setPosition(row);
        setDeleteDialog(true);
    }

    async function save() {
        if (!position.name.trim() || !position.section.trim()) {
            setApiError('Nome e seção são obrigatórios.');
            return;
        }
        setApiError('');
        const isEdit = !!position.id;
        const url = isEdit
            ? `${properties.baseURL}${properties.boardPositionsPath}/${position.id}`
            : `${properties.baseURL}${properties.boardPositionsPath}`;
        try {
            const res = await apiFetch(url, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: position.name,
                    section: position.section,
                    displayOrder: position.displayOrder ?? 0,
                }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Erro ${res.status}`);
            }
            toast.current.show({
                severity: 'success', summary: 'Sucesso',
                detail: isEdit ? 'Cargo atualizado' : 'Cargo criado', life: 3000,
            });
            setDialog(false);
            load();
        } catch (e) {
            setApiError(e.message);
        }
    }

    async function deletePosition() {
        setDeleteDialog(false);
        try {
            const res = await apiFetch(
                `${properties.baseURL}${properties.boardPositionsPath}/${position.id}`,
                { method: 'DELETE' }
            );
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                const detail = res.status === 400
                    ? `O cargo "${position.name}" está em uso e não pode ser removido.`
                    : (body.error || `Erro ${res.status}`);
                throw new Error(detail);
            }
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Cargo removido', life: 3000 });
            load();
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: e.message, life: 6000 });
        }
    }

    const actionBody = (row) => (
        <>
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2"
                onClick={() => openEdit(row)} tooltip="Editar" />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-danger"
                onClick={() => openDelete(row)} tooltip="Excluir" />
        </>
    );

    const dialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setDialog(false)} />
            <Button label="Salvar" icon="pi pi-check" onClick={save} />
        </>
    );

    const deleteDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteDialog(false)} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={deletePosition} />
        </>
    );

    return (
        <div className="datatable-crud-demo surface-card p-4 border-round shadow-2">
            <Toast ref={toast} />

            <div className="flex justify-content-between align-items-center mb-4">
                <span className="text-3xl text-800 font-bold">Cargos</span>
                <Button icon="pi pi-plus" label="Novo cargo" className="p-button-rounded" onClick={openNew} />
            </div>

            <DataTable value={positions} dataKey="id" responsiveLayout="scroll" sortField="displayOrder" sortOrder={1}>
                <Column field="section" header="Seção" sortable />
                <Column field="displayOrder" header="Ordem" sortable style={{ width: '6rem' }} />
                <Column field="name" header="Nome" sortable />
                <Column body={actionBody} exportable={false} style={{ width: '8rem' }} />
            </DataTable>

            <Dialog
                visible={dialog}
                style={{ width: '420px' }}
                header={position.id ? 'Editar cargo' : 'Novo cargo'}
                modal className="p-fluid"
                footer={dialogFooter}
                onHide={() => setDialog(false)}
            >
                {apiError && (
                    <Message severity="error" text={apiError} className="w-full mb-3"
                        style={{ justifyContent: 'flex-start' }} />
                )}
                <Fieldset>
                    <div className="field">
                        <label htmlFor="posName">Nome</label>
                        <InputText id="posName" value={position.name}
                            onChange={e => setPosition({ ...position, name: e.target.value })}
                            placeholder="Ex.: Presidente" />
                    </div>
                    <div className="field">
                        <label htmlFor="posSection">Seção</label>
                        <InputText id="posSection" value={position.section}
                            onChange={e => setPosition({ ...position, section: e.target.value })}
                            placeholder="Ex.: Presidência" />
                    </div>
                    <div className="field">
                        <label htmlFor="posOrder">Ordem de exibição</label>
                        <InputNumber id="posOrder" value={position.displayOrder}
                            onValueChange={e => setPosition({ ...position, displayOrder: e.value ?? 0 })}
                            min={0} />
                    </div>
                </Fieldset>
            </Dialog>

            <Dialog
                visible={deleteDialog}
                style={{ width: '450px' }}
                header="Confirmar exclusão"
                modal
                footer={deleteDialogFooter}
                onHide={() => setDeleteDialog(false)}
            >
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Remover o cargo <b>{position.name}</b>?<br />
                        <small className="text-500">Não será possível remover se estiver em uso em alguma gestão.</small>
                    </span>
                </div>
            </Dialog>
        </div>
    );
}
